import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, ArrowUpRight } from "lucide-react";

/**
 * ExitIntent — desktop modal triggered when the cursor leaves the viewport
 * via the top edge after the user has been on the page for at least 8 s.
 * Mobile fallback: triggered after 60 s of engaged time on the page.
 *
 * One-shot per browser session (sessionStorage). Hidden on /admin, /login,
 * /auth/callback to avoid disturbing internal flows.
 */
const STORAGE_KEY = "echo_exit_intent_shown_v1";
const ROUTE_BLACKLIST = ["/admin", "/login", "/auth"];

const isMobile = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(pointer: coarse)").matches || window.innerWidth < 768);

const ExitIntent = ({ lang = "fr" }) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const blocked = ROUTE_BLACKLIST.some((p) => location.pathname.startsWith(p));

  useEffect(() => {
    if (blocked) return undefined;
    if (typeof window === "undefined") return undefined;
    if (window.sessionStorage.getItem(STORAGE_KEY)) return undefined;

    let armed = false;
    let triggered = false;
    const armTimer = setTimeout(() => { armed = true; }, 8000);

    const trigger = () => {
      if (triggered) return;
      triggered = true;
      try { window.sessionStorage.setItem(STORAGE_KEY, "1"); } catch { /* no-op */ }
      setOpen(true);
    };

    let mobileTimer;
    const onMouseLeave = (e) => {
      if (!armed || triggered) return;
      // Only the top edge of the viewport — classic exit-intent signal.
      if (e.clientY <= 0) trigger();
    };

    if (isMobile()) {
      // 60 s engagement timer on mobile (no mouseleave on touch devices)
      mobileTimer = setTimeout(() => { if (armed) trigger(); }, 60000);
    } else {
      document.documentElement.addEventListener("mouseleave", onMouseLeave);
    }

    return () => {
      clearTimeout(armTimer);
      clearTimeout(mobileTimer);
      document.documentElement.removeEventListener("mouseleave", onMouseLeave);
    };
  }, [blocked]);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (blocked || !open) return null;

  const isEn = lang === "en";

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center"
      data-testid="exit-intent"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-ink/70 backdrop-blur-[2px]"
        onClick={() => setOpen(false)}
        data-testid="exit-intent-backdrop"
      />
      <div className="relative bg-limestone max-w-xl w-[calc(100%-2rem)] mx-4 p-8 md:p-12 shadow-2xl border-l-4 border-terracotta fade-in-up">
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label={isEn ? "close" : "fermer"}
          className="absolute top-4 right-4 text-ink/50 hover:text-terracotta transition-colors"
          data-testid="exit-intent-close"
        >
          <X size={20} strokeWidth={1.5} />
        </button>
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-terracotta mb-4">
          {isEn ? "before you go" : "avant de partir"}
        </div>
        <h2 className="font-serif text-3xl md:text-4xl text-ink leading-[1.05] mb-6 font-light">
          {isEn
            ? "Leaving without talking about your project?"
            : "Vous partez sans avoir parlé de votre projet ?"}
        </h2>
        <p className="font-sans text-base md:text-[17px] text-ink/80 leading-relaxed mb-8">
          {isEn
            ? "1h30 advice visit on site, no commitment. Written feedback within 7 days with an indicative budget envelope. We'll see if your project makes sense — together."
            : "Visite conseil 1h30 sur place, sans engagement. Retour écrit sous 7 jours avec une fourchette budgétaire indicative. On regarde si votre projet a du sens — ensemble."}
        </p>
        <div className="flex flex-wrap items-center gap-4">
          <a
            href={location.pathname === "/" ? "#contact" : "/#contact"}
            onClick={() => setOpen(false)}
            className="btn-primary"
            data-testid="exit-intent-cta"
          >
            {isEn ? "Book the visit" : "Réserver la visite"}
            <ArrowUpRight size={14} strokeWidth={1.5} />
          </a>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="font-mono text-[11px] tracking-[0.2em] uppercase text-ink/60 hover:text-ink transition-colors px-2"
            data-testid="exit-intent-dismiss"
          >
            {isEn ? "Maybe later" : "Plus tard"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExitIntent;
